import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Button, Grid,
    MenuItem, Chip, CircularProgress, Alert, Card, CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Cake as CakeIcon } from '@mui/icons-material';
import api from '../services/api';

function Aniversariantes() {
    const navigate = useNavigate();
    const [aniversariantes, setAniversariantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mesSelecionado, setMesSelecionado] = useState('');
    const [diasProximos, setDiasProximos] = useState('30');

    useEffect(() => {
        loadAniversariantes();
    }, [mesSelecionado, diasProximos]);

    const loadAniversariantes = async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (mesSelecionado) {
                params.append('mes', mesSelecionado);
            } else if (diasProximos) {
                params.append('dias_proximos', diasProximos);
            }

            console.log('[FRONTEND] Chamando /clientes/aniversariantes com params:', params.toString());
            const response = await api.get(`/clientes/aniversariantes?${params.toString()}`);
            console.log('[FRONTEND] Resposta recebida:', response.data);
            setAniversariantes(response.data || []);
        } catch (error) {
            console.error('[FRONTEND] Erro ao carregar aniversariantes:', error);
            console.error('[FRONTEND] Detalhes do erro:', error.response?.data);
            console.error('[FRONTEND] Status do erro:', error.response?.status);
            setError('Erro ao carregar aniversariantes');
        } finally {
            setLoading(false);
        }
    };

    const formatAniversario = (dia, mes) => {
        const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        return `${dia} de ${meses[mes - 1]}`;
    };

    const meses = [
        { value: '', label: 'Todos os meses' },
        { value: '1', label: 'Janeiro' },
        { value: '2', label: 'Fevereiro' },
        { value: '3', label: 'Março' },
        { value: '4', label: 'Abril' },
        { value: '5', label: 'Maio' },
        { value: '6', label: 'Junho' },
        { value: '7', label: 'Julho' },
        { value: '8', label: 'Agosto' },
        { value: '9', label: 'Setembro' },
        { value: '10', label: 'Outubro' },
        { value: '11', label: 'Novembro' },
        { value: '12', label: 'Dezembro' }
    ];

    return (
        <Container maxWidth="lg">
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <CakeIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Aniversariantes
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Lista de clientes que fazem aniversário (excluindo falecidos)
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            select
                            label="Filtrar por Mês"
                            value={mesSelecionado}
                            onChange={(e) => {
                                setMesSelecionado(e.target.value);
                                if (e.target.value) {
                                    setDiasProximos('');
                                }
                            }}
                        >
                            {meses.map((mes) => (
                                <MenuItem key={mes.value} value={mes.value}>
                                    {mes.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Próximos N dias"
                            type="number"
                            value={diasProximos}
                            onChange={(e) => {
                                setDiasProximos(e.target.value);
                                if (e.target.value) {
                                    setMesSelecionado('');
                                }
                            }}
                            disabled={!!mesSelecionado}
                            helperText={mesSelecionado ? 'Desmarque o mês para usar este filtro' : 'Mostra aniversários nos próximos N dias'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                setMesSelecionado('');
                                setDiasProximos('30');
                            }}
                        >
                            Limpar Filtros
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>Data de Nascimento</TableCell>
                            <TableCell>Aniversário</TableCell>
                            <TableCell>Idade Atual</TableCell>
                            <TableCell>Idade que fará</TableCell>
                            <TableCell>Dias até aniversário</TableCell>
                            <TableCell>Telefone</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : aniversariantes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    Nenhum aniversariante encontrado
                                </TableCell>
                            </TableRow>
                        ) : (
                            aniversariantes.map((aniversariante) => (
                                <TableRow
                                    key={aniversariante.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/clientes/editar/${aniversariante.id}`)}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CakeIcon color="secondary" />
                                            <strong>{aniversariante.nome}</strong>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{aniversariante.cpf || '-'}</TableCell>
                                    <TableCell>
                                        {aniversariante.data_nascimento || '-'}
                                    </TableCell>
                                    <TableCell>
                                        {formatAniversario(aniversariante.dia_aniversario, aniversariante.mes_aniversario)}
                                    </TableCell>
                                    <TableCell>{aniversariante.idade_atual} anos</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={`${aniversariante.idade_proxima} anos`}
                                            size="small"
                                            color={aniversariante.dias_ate_aniversario === 0 ? 'error' : 'primary'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {aniversariante.dias_ate_aniversario === 0 ? (
                                            <Chip label="Hoje!" size="small" color="error" />
                                        ) : aniversariante.dias_ate_aniversario === 1 ? (
                                            <Chip label="Amanhã" size="small" color="warning" />
                                        ) : (
                                            <Chip label={`${aniversariante.dias_ate_aniversario} dias`} size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell>{aniversariante.telefone || '-'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {aniversariantes.length > 0 && (
                <Card sx={{ mt: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Resumo
                        </Typography>
                        <Typography variant="body2">
                            Total de aniversariantes: <strong>{aniversariantes.length}</strong>
                        </Typography>
                        {aniversariantes.filter(a => a.dias_ate_aniversario === 0).length > 0 && (
                            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                                Aniversários hoje: <strong>{aniversariantes.filter(a => a.dias_ate_aniversario === 0).length}</strong>
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            )}
        </Container>
    );
}

export default Aniversariantes;

